import React, {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button, Modal, Spin, Typography } from 'antd';
import { useSelector } from 'react-redux';
import {
  useDismissSystemOutageMutation,
  useGetSystemOutageStatusQuery,
} from '@/common/system-outage/system-outage.query';
import '@/common/system-outage/system-outage.less';

const { Paragraph, Text, Title } = Typography;

export const SYSTEM_OUTAGE_STATE = Object.freeze({
  NONE: 0,
  SCHEDULED: 1,
  ACTIVE: 2,
  COMPLETED: 3,
  CANCELLED: 4,
});

const SystemOutageContext = createContext(null);

const stateMatches = (state, numericState, textState) => (
  state === numericState || String(state).toLowerCase() === textState.toLowerCase()
);

export const getMaintenanceCountdown = (status, now = Date.now()) => {
  const durationMinutes = Math.max(1, Number(status?.expectedDurationMinutes) || 5);
  const durationMilliseconds = durationMinutes * 60 * 1000;
  const parsedStart = Date.parse(status?.maintenanceStartedAtUtc);
  const startedAt = Number.isFinite(parsedStart) ? parsedStart : now;
  const elapsed = Math.max(0, now - startedAt);
  const elapsedInCycle = elapsed % durationMilliseconds;
  const remainingSeconds = Math.max(
    1,
    Math.ceil((durationMilliseconds - elapsedInCycle) / 1000)
  );

  return {
    delayed: elapsed >= durationMilliseconds,
    minutes: Math.floor(remainingSeconds / 60),
    seconds: remainingSeconds % 60,
  };
};

const formatCountdown = ({ minutes, seconds }) => (
  `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
);

const defaultRecovery = () => window.location.reload();

export const useSystemOutageStatus = () => useContext(SystemOutageContext);

const InitialStatusCheck = () => (
  <div className="system-outage-initial-check" role="status" aria-live="polite">
    <Spin size="large" />
    <Text>Checking system availability…</Text>
  </div>
);

const ApplicationLoading = () => (
  <div className="system-outage-initial-check" role="status" aria-live="polite">
    <Spin size="large" />
    <Text>Loading application…</Text>
  </div>
);

export const UploadFreezeNotice = ({ status }) => {
  if (!status?.uploadsBlocked) {
    return null;
  }

  const active = status.isMaintenanceActive
    || stateMatches(status.state, SYSTEM_OUTAGE_STATE.ACTIVE, 'Active');
  const description = active
    ? `${status.systemName || 'This system'} is currently unavailable while maintenance is completed.`
    : `DMS is preventing a new final upload submission ahead of maintenance${status.plannedStartDisplay ? ` at ${status.plannedStartDisplay}` : ''}. A final submission already in progress may finish its remaining sequential batches before maintenance begins.`;

  return (
    <div className="system-outage-upload-freeze">
      <Alert
        type="warning"
        showIcon
        message="Starting final upload submission is temporarily paused"
        description={description}
      />
    </div>
  );
};

const ScheduledOutageNotice = ({ status }) => {
  const [dismiss, { isLoading, isError }] = useDismissSystemOutageMutation();
  const [locallyDismissedId, setLocallyDismissedId] = useState(null);
  const isLoggedIn = useSelector((state) => Boolean(state.userDetails?.isLogin));
  const scheduled = stateMatches(
    status?.state,
    SYSTEM_OUTAGE_STATE.SCHEDULED,
    'Scheduled'
  );
  const open = Boolean(
    isLoggedIn
      && scheduled
      && status?.occurrenceId
      && !status.isDismissed
      && locallyDismissedId !== status.occurrenceId
  );

  const handleDismiss = async () => {
    try {
      await dismiss(status.occurrenceId).unwrap();
      setLocallyDismissedId(status.occurrenceId);
    } catch {
      // The inline alert keeps the notice open so the user can retry.
    }
  };

  return (
    <Modal
      open={open}
      title={`${status?.systemName || 'System'} maintenance notice`}
      closable={false}
      keyboard={false}
      maskClosable={false}
      width={600}
      className="system-outage-notice-modal"
      footer={(
        <Button type="primary" loading={isLoading} onClick={handleDismiss}>
          Close and do not show again for this outage
        </Button>
      )}
    >
      <Paragraph>
        <Text strong>{status?.systemName || 'This system'}</Text> will be unavailable for approximately{' '}
        {status?.expectedDurationMinutes || 5} minutes for system maintenance.
      </Paragraph>
      {status?.plannedStartDisplay && (
        <Paragraph>
          <Text strong>Planned start:</Text> {status.plannedStartDisplay}
        </Paragraph>
      )}
      {status?.reason && (
        <Paragraph>
          <Text strong>Reason:</Text> {status.reason}
        </Paragraph>
      )}
      {status?.uploadsBlocked && (
        <Alert
          type="warning"
          showIcon
          message="Starting final upload submission is now paused until maintenance is complete. A final submission already in progress may finish its remaining sequential batches before maintenance begins."
        />
      )}
      {isError && (
        <Alert
          className="system-outage-notice-error"
          type="error"
          showIcon
          message="We could not save your choice. Please try again."
        />
      )}
    </Modal>
  );
};

const ActiveMaintenanceScreen = ({ status }) => {
  const [now, setNow] = useState(() => Date.now());
  const isEmergency = Boolean(status?.isEmergency);
  const countdown = useMemo(
    () => getMaintenanceCountdown(status, now),
    [now, status]
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    document.body.classList.add('system-outage-active');

    return () => {
      window.clearInterval(timer);
      document.body.classList.remove('system-outage-active');
    };
  }, []);

  return (
    <div
      className="system-outage-screen"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="system-outage-active-heading"
      aria-describedby="system-outage-active-summary system-outage-active-checking"
    >
      <main className="system-outage-card">
        <div className="system-outage-icon" aria-hidden="true">
          <i className="ri-tools-line" />
        </div>
        <Text className="system-outage-eyebrow">
          {isEmergency ? 'Emergency system maintenance' : 'Planned system maintenance'}
        </Text>
        <Title id="system-outage-active-heading" level={1}>
          {status.systemName || 'This system'} is temporarily unavailable
        </Title>
        <Paragraph id="system-outage-active-summary" className="system-outage-summary">
          {isEmergency
            ? 'We are responding to an urgent system issue and expect service to return shortly.'
            : 'We are completing planned system maintenance and expect service to return shortly.'}
        </Paragraph>
        {status.reason && <Paragraph className="system-outage-reason">{status.reason}</Paragraph>}

        <section className="system-outage-countdown" role="timer" aria-label="Maintenance time estimate">
          <Text>{countdown.delayed ? 'Next service check window' : 'Estimated time remaining'}</Text>
          <div className="system-outage-clock">{formatCountdown(countdown)}</div>
        </section>

        {countdown.delayed && (
          <Alert
            type="warning"
            showIcon
            message="Sorry, this is taking longer than expected. We are working on it."
          />
        )}

        <Paragraph id="system-outage-active-checking" className="system-outage-checking">
          <span className="system-outage-pulse" aria-hidden="true" />
          Checking every {status.retryAfterSeconds || 15} seconds. This page will refresh when the system is ready.
        </Paragraph>

        <footer>
          Need help? Email{' '}
          <a href={`mailto:${status.supportEmail}`}>{status.supportEmail}</a>
          {status.supportPhone && (
            <> or call <a href={`tel:${status.supportPhone.replace(/[^+\d]/g, '')}`}>{status.supportPhone}</a></>
          )}.
        </footer>
      </main>
    </div>
  );
};

const SystemOutageExperience = ({ children, onRecovered = defaultRecovery }) => {
  const statusQueryKey = useSelector((state) => {
    if (!state.userDetails?.isLogin) return 'anonymous';
    return `user:${state.userDetails?.info?.id || 'current'}`;
  });
  const { data: status, isLoading } = useGetSystemOutageStatusQuery(statusQueryKey, {
    pollingInterval: 15000,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const wasActive = useRef(false);
  const active = Boolean(
    status?.isMaintenanceActive
      || stateMatches(status?.state, SYSTEM_OUTAGE_STATE.ACTIVE, 'Active')
  );

  useEffect(() => {
    if (wasActive.current && status && !active) {
      onRecovered();
    }
    wasActive.current = active;
  }, [active, onRecovered, status]);

  return (
    <SystemOutageContext.Provider value={status || null}>
      {!status && isLoading ? (
        <InitialStatusCheck />
      ) : active ? (
        <ActiveMaintenanceScreen status={status} />
      ) : (
        <>
          <Suspense fallback={<ApplicationLoading />}>
            {children}
          </Suspense>
          <ScheduledOutageNotice status={status} />
        </>
      )}
    </SystemOutageContext.Provider>
  );
};

export default SystemOutageExperience;
